import { ChangeDetectionStrategy, Component, computed, input, inject, InjectionToken, TemplateRef } from '@angular/core';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation, slideInUpOnEnterAnimation, slideOutDownOnLeaveAnimation, slideInDownOnEnterAnimation, slideOutUpOnLeaveAnimation, slideInLeftOnEnterAnimation, slideOutRightOnLeaveAnimation, slideInRightOnEnterAnimation, slideOutLeftOnLeaveAnimation } from '../animations/index';
import { NgTemplateOutlet } from '@angular/common';

export const TOOLTIP_CONTENT = new InjectionToken<string | null>('TOOLTIP_CONTENT');
export const TOOLTIP_TEMPLATE = new InjectionToken<TemplateRef<unknown>>('TOOLTIP_TEMPLATE');
export const TOOLTIP_DATA = new InjectionToken<unknown>('TOOLTIP_DATA');

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet],
    host: {
        '[@.disabled]': 'reducedMotion'
    },
    animations: [
        fadeInOnEnterAnimation({ duration: 150 }),
        fadeOutOnLeaveAnimation({ duration: 120 }),
        slideInUpOnEnterAnimation({ duration: 150, translate: '4px' }),
        slideOutDownOnLeaveAnimation({ duration: 120, translate: '4px' }),
        slideInDownOnEnterAnimation({ duration: 150, translate: '4px' }),
        slideOutUpOnLeaveAnimation({ duration: 120, translate: '4px' }),
        slideInLeftOnEnterAnimation({ duration: 150, translate: '4px' }),
        slideOutRightOnLeaveAnimation({ duration: 120, translate: '4px' }),
        slideInRightOnEnterAnimation({ duration: 150, translate: '4px' }),
        slideOutLeftOnLeaveAnimation({ duration: 120, translate: '4px' })
    ]
})
export class TooltipComponent {
  private static nextId = 0;
  /** Id of the tooltip text, for the trigger's aria-describedby. */
  readonly id = `if-tooltip-${TooltipComponent.nextId++}`;
  /** Animations (Angular, not CSS) are switched off under reduced motion. */
  readonly reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  content = input<string | null>(null);
  contentTemplate = input<TemplateRef<unknown> | null>(null);
  maxWidth = input('400px');
  position = input('top');

  private injectedContent = inject(TOOLTIP_CONTENT, { optional: true });
  injectedTemplate = inject(TOOLTIP_TEMPLATE, { optional: true });
  private injectedData = inject(TOOLTIP_DATA, { optional: true });

  displayContent = computed(() => this.injectedContent ?? this.content());

  displayTemplate = computed(() => this.injectedTemplate || this.contentTemplate());

  isTemplate = computed(() => !!this.displayTemplate());

  animationName = computed(() => {
    switch (this.position()) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return 'slideInUpOnEnter';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return 'slideInDownOnEnter';
      case 'left':
      case 'left-start':
      case 'left-end':
        return 'slideInLeftOnEnter';
      case 'right':
      case 'right-start':
      case 'right-end':
        return 'slideInRightOnEnter';
      default:
        return 'fadeInOnEnter';
    }
  });

  leaveAnimationName = computed(() => {
    switch (this.position()) {
      case 'top':
      case 'top-start':
      case 'top-end':
        return 'slideOutDownOnLeave';
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        return 'slideOutUpOnLeave';
      case 'left':
      case 'left-start':
      case 'left-end':
        return 'slideOutRightOnLeave';
      case 'right':
      case 'right-start':
      case 'right-end':
        return 'slideOutLeftOnLeave';
      default:
        return 'fadeOutOnLeave';
    }
  });
}
